import {
  MAX_MESSAGE_LENGTH,
  PRESENCE_DEFINITIONS,
  RED_LINE_CLOSING_PRESETS,
  RED_LINE_OPEN_DURATIONS,
  RED_LINE_SIGNAL_REACTIONS,
  type AttachmentView,
  type LocalSummary,
  type PublicPresence,
  type RealtimeEvent,
  type RealtimeTransportState,
  type RedLineAvailabilityState,
  type RedLineMessageView,
  type RedLineSignalReaction,
  type SignalPortalConfig,
  type SignalPortalConfigUpdate,
} from '@deffy-direct/contracts';
import { ConversationRealtimeClient } from '@deffy-direct/realtime';
import { Badge, BrandMark, Button } from '@deffy-direct/ui';
import {
  Activity,
  AudioLines,
  Bell,
  Bug,
  Check,
  ChevronDown,
  CircleAlert,
  Clock3,
  Eye,
  EyeOff,
  FileText,
  Gauge,
  Inbox,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  MessageCircle,
  MoreHorizontal,
  Pause,
  Paperclip,
  Pin,
  PinOff,
  Radio,
  RefreshCw,
  Search,
  Send,
  Server,
  Shield,
  Signal,
  Sparkles,
  Volume2,
  VolumeX,
  Video,
  Wifi,
  WifiOff,
  Download,
  X,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';

import {
  adminApi,
  AdminApiFailure,
  API_BASE_URL,
  setUnauthorizedHandler,
  type AdminConversation,
  type BugReportItem,
  type ConversationListItem,
  type Overview,
  type RequestDetail,
  type RequestListItem,
} from './api';
import {
  evaluateAndDeliverAdminAlerts,
  loadAdminNotificationSettings,
  notificationPermission,
  primeNotificationSound,
  requestNotificationPermission,
  saveAdminNotificationSettings,
  sendAdminNotificationTest,
  type AdminNotificationSettings,
} from './admin-notifications';
import { refreshPendingRequests, usePendingRequests } from './use-pending-requests';
import {
  cachedLocalSummary,
  checkLocalAi,
  clearLocalSummary,
  loadLocalAiSettings,
  saveLocalAiSettings,
  summarizeRequest,
  type LocalAiHealth,
  type LocalAiSettings,
} from './local-ai';
import { Markdown } from './Markdown';

type AuthState = 'checking' | 'signed-out' | 'signed-in';
type DashboardView = 'inbox' | 'conversations' | 'bugs' | 'system' | 'red-line' | 'signal-portal';

function errorText(error: unknown): string {
  return error instanceof AdminApiFailure
    ? error.message
    : 'The console hit an unexpected problem.';
}

function relativeTime(iso: string, now = Date.now()): string {
  const seconds = Math.max(0, Math.floor((now - Date.parse(iso)) / 1_000));
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3_600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86_400)
    return `${Math.floor(seconds / 3_600)}h ${Math.floor((seconds % 3_600) / 60)}m`;
  return `${Math.floor(seconds / 86_400)}d`;
}

export function App() {
  const [auth, setAuth] = useState<AuthState>('checking');
  const [view, setView] = useState<DashboardView>('inbox');
  const [requestTarget, setRequestTarget] = useState<string | null>(null);
  const [conversationTarget, setConversationTarget] = useState<string | null>(null);

  useEffect(() => {
    setUnauthorizedHandler(() => setAuth('signed-out'));
    void adminApi
      .session()
      .then(() => setAuth('signed-in'))
      .catch(() => setAuth('signed-out'));
  }, []);

  if (auth === 'checking') return <ConsoleBoot />;
  if (auth === 'signed-out') return <Login onAuthenticated={() => setAuth('signed-in')} />;

  return (
    <Dashboard
      view={view}
      onView={(next) => {
        setView(next);
        if (next !== 'inbox') setRequestTarget(null);
        if (next !== 'conversations') setConversationTarget(null);
      }}
      requestTarget={requestTarget}
      conversationTarget={conversationTarget}
      openRequest={(publicId) => {
        setRequestTarget(publicId);
        setConversationTarget(null);
        setView('inbox');
      }}
      openConversation={(publicId) => {
        setRequestTarget(null);
        setConversationTarget(publicId);
        setView('conversations');
      }}
      onSignedOut={() => setAuth('signed-out')}
    />
  );
}

function ConsoleBoot() {
  return (
    <div className="console-boot" role="status">
      <span className="console-boot__node">
        <Radio />
      </span>
      <strong>Securing the private node…</strong>
    </div>
  );
}

function Login({ onAuthenticated }: { onAuthenticated: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await adminApi.login(password);
      setPassword('');
      onAuthenticated();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="login-screen">
      <div className="login-atmosphere" aria-hidden="true" />
      <section className="login-card" aria-labelledby="login-title">
        <BrandMark />
        <div className="login-lock" aria-hidden="true">
          <LockKeyhole />
        </div>
        <small>PRIVATE COMMUNICATIONS NODE</small>
        <h1 id="login-title">Enter the creature console.</h1>
        <p>One admin. No public account surface. Every attempt is rate-limited and audited.</p>
        <form onSubmit={submit}>
          <label>
            <span>Admin password</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              minLength={12}
              required
              autoFocus
            />
          </label>
          {error ? (
            <div className="login-error" role="alert">
              {error}
            </div>
          ) : null}
          <Button type="submit" busy={busy}>
            Unlock console
          </Button>
        </form>
        <span className="login-footnote">
          <Shield size={14} /> Session cookies are HttpOnly, scoped, and short-lived.
        </span>
      </section>
    </main>
  );
}

function Dashboard({
  view,
  onView,
  requestTarget,
  conversationTarget,
  openRequest,
  openConversation,
  onSignedOut,
}: {
  view: DashboardView;
  onView: (view: DashboardView) => void;
  requestTarget: string | null;
  conversationTarget: string | null;
  openRequest: (publicId: string) => void;
  openConversation: (publicId: string) => void;
  onSignedOut: () => void;
}) {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [localAi, setLocalAi] = useState<LocalAiHealth>(() => ({
    state: 'checking',
    settings: loadLocalAiSettings(),
    models: [],
    detail: 'Checking the local Ollama service…',
  }));
  const {
    requests: alertRequests,
    error: pendingError,
    refresh: refreshPending,
  } = usePendingRequests();
  const [notificationSettings, setNotificationSettings] = useState<AdminNotificationSettings>(() =>
    loadAdminNotificationSettings(),
  );
  const [mobileNav, setMobileNav] = useState(false);

  const refreshOverview = useCallback(async () => {
    try {
      const result = await adminApi.overview();
      setOverview(result);
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);

  useEffect(() => {
    void refreshOverview();
    const interval = window.setInterval(() => void refreshOverview(), 5_000);
    return () => window.clearInterval(interval);
  }, [refreshOverview]);

  const refreshLocalAi = useCallback(async () => {
    setLocalAi((current) => ({ ...current, state: 'checking' }));
    setLocalAi(await checkLocalAi());
  }, []);

  useEffect(() => {
    void refreshLocalAi();
    const interval = window.setInterval(() => void refreshLocalAi(), 30_000);
    return () => {
      window.clearInterval(interval);
    };
  }, [refreshLocalAi]);

  useEffect(() => {
    evaluateAndDeliverAdminAlerts(alertRequests, notificationSettings, openRequest);
  }, [alertRequests, notificationSettings, openRequest]);

  const logout = async () => {
    try {
      await adminApi.logout();
    } finally {
      onSignedOut();
    }
  };

  return (
    <div
      className={`dashboard-shell ${notificationSettings.reducedSensory ? 'dashboard-shell--reduced-sensory' : ''}`}
    >
      <a className="admin-skip" href="#dashboard-main">
        Skip to dashboard
      </a>
      <aside className={`dashboard-sidebar ${mobileNav ? 'dashboard-sidebar--open' : ''}`}>
        <div className="sidebar-brand">
          <BrandMark />
          <Badge tone="purple">Admin</Badge>
        </div>
        <nav aria-label="Private console">
          <NavButton
            active={view === 'inbox'}
            onClick={() => {
              onView('inbox');
              setMobileNav(false);
            }}
            icon={<Inbox />}
            label="Requests"
            count={overview?.counts.pendingRequests}
          />
          <NavButton
            active={view === 'conversations'}
            onClick={() => {
              onView('conversations');
              setMobileNav(false);
            }}
            icon={<MessageCircle />}
            label="Conversations"
            count={overview?.counts.unreadMessages}
          />
          <NavButton
            active={view === 'bugs'}
            onClick={() => {
              onView('bugs');
              setMobileNav(false);
            }}
            icon={<Bug />}
            label="Bug reports"
            count={overview?.counts.openBugReports}
          />
          <NavButton
            active={view === 'red-line'}
            onClick={() => {
              onView('red-line');
              setMobileNav(false);
            }}
            icon={<Radio />}
            label="Red Line"
          />
          <NavButton
            active={view === 'signal-portal'}
            onClick={() => {
              onView('signal-portal');
              setMobileNav(false);
            }}
            icon={<Signal />}
            label="Signal Portal"
          />
          <NavButton
            active={view === 'system'}
            onClick={() => {
              onView('system');
              setMobileNav(false);
            }}
            icon={<Gauge />}
            label="System"
          />
        </nav>
        <div className="sidebar-health">
          <span>
            <i
              className={
                localAi.state === 'ready'
                  ? 'ok'
                  : localAi.state === 'offline' || localAi.state === 'misconfigured'
                    ? 'bad'
                    : ''
              }
            />{' '}
            Ollama {localAi.state.replace('_', ' ')}
          </span>
          <span>
            <i className={error || pendingError ? 'bad' : 'ok'} /> API{' '}
            {error || pendingError ? 'degraded' : 'connected'}
          </span>
        </div>
        <button className="sidebar-logout" onClick={() => void logout()}>
          <LogOut /> Log out
        </button>
      </aside>

      <div className="dashboard-body">
        <header className="dashboard-topbar">
          <button
            className="mobile-menu"
            onClick={() => setMobileNav((value) => !value)}
            aria-expanded={mobileNav}
            aria-label="Toggle navigation"
          >
            <MoreHorizontal />
          </button>
          <div className="topbar-title">
            <small>DEFFY DIRECT</small>
            <strong>
              {view === 'inbox'
                ? 'Request threshold'
                : view === 'conversations'
                  ? 'Open direct lines'
                  : view === 'bugs'
                    ? 'Bug signals'
                    : view === 'red-line'
                      ? 'Red Line'
                      : view === 'signal-portal'
                        ? 'Signal Portal'
                        : 'System node'}
            </strong>
          </div>
          <PresenceControl
            presence={overview?.presence ?? null}
            onChanged={() => void refreshOverview()}
          />
          <AdminNotificationNode
            requests={alertRequests}
            localAi={localAi}
            onPreview={openRequest}
            onConversation={openConversation}
            onChanged={() => {
              void refreshPending();
              void refreshOverview();
            }}
          />
        </header>

        <main id="dashboard-main" className="dashboard-main">
          {error || pendingError ? (
            <div className="dashboard-warning">
              <WifiOff /> {error || pendingError}
            </div>
          ) : null}
          {view === 'inbox' ? (
            <InboxView
              initialPublicId={requestTarget}
              openConversation={openConversation}
              onCountsChanged={() => void refreshOverview()}
              localAi={localAi}
            />
          ) : null}
          {view === 'conversations' ? (
            <ConversationsView initialPublicId={conversationTarget} />
          ) : null}
          {view === 'bugs' ? <BugsView /> : null}
          {view === 'red-line' ? <RedLineView /> : null}
          {view === 'signal-portal' ? <SignalPortalView /> : null}
          {view === 'system' ? (
            <SystemView
              overview={overview}
              localAi={localAi}
              onLocalAiSettingsChanged={() => void refreshLocalAi()}
              notificationSettings={notificationSettings}
              onNotificationSettingsChanged={setNotificationSettings}
            />
          ) : null}
        </main>
      </div>
      {mobileNav ? (
        <button
          className="sidebar-scrim"
          onClick={() => setMobileNav(false)}
          aria-label="Close navigation"
        />
      ) : null}
    </div>
  );
}

function AdminNotificationNode({
  requests,
  localAi,
  onPreview,
  onConversation,
  onChanged,
}: {
  requests: RequestListItem[];
  localAi: LocalAiHealth;
  onPreview: (publicId: string) => void;
  onConversation: (publicId: string) => void;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [presets, setPresets] = useState<Array<{ id: string; label: string; description: string }>>(
    [],
  );
  const [rejectionReason, setRejectionReason] = useState<Record<string, string>>({});
  const [summaries, setSummaries] = useState<Record<string, LocalSummary>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const root = useRef<HTMLDivElement>(null);
  const hoverTimer = useRef<number | null>(null);
  const summarizing = useRef(new Set<string>());

  const closeTimer = () => {
    if (hoverTimer.current !== null) window.clearTimeout(hoverTimer.current);
    hoverTimer.current = null;
  };

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => {
      document.removeEventListener('mousedown', close);
      closeTimer();
    };
  }, []);

  useEffect(() => {
    if (!open || presets.length > 0) return;
    void adminApi
      .rejectionPresets()
      .then((result) => setPresets(result.presets))
      .catch((caught: unknown) => setError(errorText(caught)));
  }, [open, presets.length]);

  useEffect(() => {
    if (!open || localAi.state !== 'ready') return;
    for (const request of requests.slice(0, 4)) {
      const cached = cachedLocalSummary(request.publicId);
      if (cached) {
        setSummaries((current) => ({ ...current, [request.publicId]: cached }));
        continue;
      }
      if (summaries[request.publicId] || summarizing.current.has(request.publicId)) {
        continue;
      }
      summarizing.current.add(request.publicId);
      void adminApi
        .request(request.publicId)
        .then((result) =>
          summarizeRequest({
            requestPublicId: result.request.publicId,
            alias: result.request.alias,
            selectedIntent: result.request.intent,
            selectedUrgency: result.request.urgency,
            messages: result.request.messages.map((message) => message.bodyMarkdown),
          }),
        )
        .then((summary) => setSummaries((current) => ({ ...current, [request.publicId]: summary })))
        .catch(() => undefined)
        .finally(() => summarizing.current.delete(request.publicId));
    }
  }, [localAi.state, open, requests, summaries]);

  const action = async (request: RequestListItem, kind: 'seen' | 'accept' | 'reject') => {
    const actionKey = `${request.publicId}:${kind}`;
    setBusy(actionKey);
    setError(null);
    try {
      if (kind === 'seen') {
        await adminApi.acknowledgeRequest(request.publicId);
      } else if (kind === 'accept') {
        const result = await adminApi.acceptRequest(request.publicId);
        setOpen(false);
        onConversation(result.conversation.publicId);
      } else {
        const reasonId = rejectionReason[request.publicId] ?? presets[0]?.id;
        if (!reasonId) throw new Error('Choose a rejection reason first.');
        await adminApi.rejectRequest(request.publicId, reasonId);
      }
      onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : errorText(caught));
    } finally {
      setBusy(null);
    }
  };

  const oldest = requests[0];
  const important = requests.some((request) => request.urgency === 'important');
  return (
    <div
      ref={root}
      className="notification-node-wrap"
      onMouseEnter={() => {
        closeTimer();
        hoverTimer.current = window.setTimeout(() => setOpen(true), 260);
      }}
      onMouseLeave={() => {
        closeTimer();
        hoverTimer.current = window.setTimeout(() => setOpen(false), 320);
      }}
      onFocus={() => setOpen(true)}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        className={`notification-node ${requests.length ? 'notification-node--active' : ''} ${important ? 'notification-node--important' : ''}`}
        onClick={() => setOpen((value) => !value)}
        aria-label={`${requests.length} waiting requests${oldest ? `, oldest waiting ${relativeTime(oldest.waitingSince)}` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls="admin-notification-popover"
      >
        <span className="notification-avatar">Ð</span>
        {requests.length > 0 ? <b>{requests.length > 99 ? '99+' : requests.length}</b> : null}
      </button>
      {open ? (
        <section
          id="admin-notification-popover"
          className="notification-popover"
          role="dialog"
          aria-label="Waiting request notifications"
        >
          <header>
            <span>
              <small>REQUEST SIGNALS</small>
              <strong>{requests.length ? `${requests.length} waiting` : 'Threshold quiet'}</strong>
            </span>
            {oldest ? <Badge tone="gold">oldest {relativeTime(oldest.waitingSince)}</Badge> : null}
          </header>
          <div className="notification-popover__list">
            {requests.length === 0 ? (
              <p>No pending requests need attention.</p>
            ) : (
              requests.slice(0, 4).map((request) => {
                const summary = summaries[request.publicId];
                const selectedReason = rejectionReason[request.publicId] ?? presets[0]?.id ?? '';
                return (
                  <article key={request.publicId}>
                    <header>
                      <span className="request-avatar">
                        {request.alias.slice(0, 1).toUpperCase()}
                      </span>
                      <span>
                        <strong>{request.alias}</strong>
                        <small>
                          {request.sourceProject.label} · #{request.actualQueuePosition} actual · #
                          {request.publicEstimatedPosition} public
                        </small>
                      </span>
                      <Badge tone={request.urgency === 'important' ? 'danger' : 'purple'}>
                        {request.urgency}
                      </Badge>
                    </header>
                    <p>{summary?.summary ?? request.firstMessageExcerpt}</p>
                    <div className="notification-popover__facts">
                      <span>{request.intent ?? 'No intent'}</span>
                      <span>{relativeTime(request.waitingSince)} waiting</span>
                      {summary ? <span>{summary.tone} tone</span> : null}
                      {request.pendingAdditionCount ? (
                        <span>{request.pendingAdditionCount} addition</span>
                      ) : null}
                      {request.attachmentCount ? (
                        <span>
                          <Paperclip /> {request.attachmentCount}
                        </span>
                      ) : null}
                      {request.returningVisitor ? <span>returning visitor</span> : null}
                    </div>
                    <div className="notification-popover__actions">
                      <button
                        onClick={() => {
                          setOpen(false);
                          onPreview(request.publicId);
                        }}
                      >
                        Preview
                      </button>
                      <button disabled={busy !== null} onClick={() => void action(request, 'seen')}>
                        Seen
                      </button>
                      <button
                        disabled={busy !== null}
                        onClick={() => void action(request, 'accept')}
                      >
                        Accept
                      </button>
                      <span>
                        <select
                          value={selectedReason}
                          onChange={(event) =>
                            setRejectionReason((current) => ({
                              ...current,
                              [request.publicId]: event.target.value,
                            }))
                          }
                          aria-label={`Rejection reason for ${request.alias}`}
                        >
                          {presets.map((preset) => (
                            <option key={preset.id} value={preset.id}>
                              {preset.label}
                            </option>
                          ))}
                        </select>
                        <button
                          disabled={busy !== null || !selectedReason}
                          onClick={() => void action(request, 'reject')}
                        >
                          Reject
                        </button>
                      </span>
                    </div>
                  </article>
                );
              })
            )}
          </div>
          {requests.length > 4 ? (
            <button
              className="notification-popover__all"
              onClick={() => {
                setOpen(false);
                onPreview(requests[0]?.publicId ?? '');
              }}
            >
              Open all {requests.length} requests
            </button>
          ) : null}
          {error ? <div className="notification-popover__error">{error}</div> : null}
        </section>
      ) : null}
    </div>
  );
}

function NavButton({
  active,
  onClick,
  icon,
  label,
  count,
}: {
  active: boolean;
  onClick: () => void;
  icon: ReactNode;
  label: string;
  count?: number | undefined;
}) {
  return (
    <button className={active ? 'active' : ''} onClick={onClick}>
      <span>{icon}</span>
      {label}
      {count ? <b>{count > 99 ? '99+' : count}</b> : null}
    </button>
  );
}

function PresenceControl({
  presence,
  onChanged,
}: {
  presence: PublicPresence | null;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [subtitle, setSubtitle] = useState('');
  const root = useRef<HTMLDivElement>(null);

  useEffect(() => setSubtitle(presence?.subtitle ?? ''), [presence?.subtitle]);
  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const update = async (preset: keyof typeof PRESENCE_DEFINITIONS) => {
    setBusy(true);
    try {
      await adminApi.updatePresence({
        preset,
        ...(subtitle.trim() ? { subtitle: subtitle.trim() } : { subtitle: null }),
      });
      setOpen(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="presence-control" ref={root}>
      <button onClick={() => setOpen((value) => !value)} aria-expanded={open}>
        <i className={`presence-${presence?.preset ?? 'building'}`} />
        <span>
          <small>PUBLIC STATUS</small>
          <strong>{presence?.label ?? 'Loading'}</strong>
        </span>
        <ChevronDown />
      </button>
      {open ? (
        <div className="presence-menu">
          <label>
            <span>Public subtitle</span>
            <input
              value={subtitle}
              onChange={(event) => setSubtitle(event.target.value)}
              maxLength={160}
            />
          </label>
          <div>
            {(Object.keys(PRESENCE_DEFINITIONS) as Array<keyof typeof PRESENCE_DEFINITIONS>).map(
              (preset) => (
                <button key={preset} disabled={busy} onClick={() => void update(preset)}>
                  <i className={`presence-${preset}`} />
                  <span>
                    <strong>{PRESENCE_DEFINITIONS[preset].label}</strong>
                    <small>{PRESENCE_DEFINITIONS[preset].expectedReply}</small>
                  </span>
                  {presence?.preset === preset ? <Check /> : null}
                </button>
              ),
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function InboxView({
  initialPublicId,
  openConversation,
  onCountsChanged,
  localAi,
}: {
  initialPublicId: string | null;
  openConversation: (publicId: string) => void;
  onCountsChanged: () => void;
  localAi: LocalAiHealth;
}) {
  const { requests: pendingRequests } = usePendingRequests();
  const [archiveRequests, setArchiveRequests] = useState<RequestListItem[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [detail, setDetail] = useState<RequestDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'pending' | 'archive'>('pending');
  const [search, setSearch] = useState('');
  const [presets, setPresets] = useState<Array<{ id: string; label: string; description: string }>>(
    [],
  );
  const [actionBusy, setActionBusy] = useState(false);

  const requests = filter === 'pending' ? pendingRequests : archiveRequests;

  useEffect(() => {
    if (presets.length > 0) return;
    void adminApi
      .rejectionPresets()
      .then((result) => setPresets(result.presets))
      .catch((caught: unknown) => setError(errorText(caught)));
  }, [presets.length]);

  const refreshArchive = useCallback(async () => {
    try {
      const result = await adminApi.requests('archive');
      setArchiveRequests(result.requests);
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);

  useEffect(() => {
    if (filter !== 'archive') return;
    void refreshArchive();
    const interval = window.setInterval(() => void refreshArchive(), 5_000);
    return () => window.clearInterval(interval);
  }, [filter, refreshArchive]);

  useEffect(() => {
    if (selected && !requests.some((request) => request.publicId === selected)) {
      setSelected(null);
      setDetail(null);
    }
  }, [selected, requests]);

  const selectRequest = useCallback(
    async (publicId: string) => {
      setSelected(publicId);
      setLoadingDetail(true);
      setError(null);
      try {
        const result = await adminApi.previewRequest(publicId);
        setDetail(result.request);
        onCountsChanged();
      } catch (caught) {
        setError(errorText(caught));
      } finally {
        setLoadingDetail(false);
      }
    },
    [onCountsChanged],
  );

  useEffect(() => {
    if (initialPublicId && initialPublicId !== selected) {
      void selectRequest(initialPublicId);
    }
  }, [initialPublicId, selectRequest, selected]);

  const accept = async () => {
    if (!selected) return;
    setActionBusy(true);
    try {
      const result = await adminApi.acceptRequest(selected);
      void refreshPendingRequests();
      if (filter === 'archive') void refreshArchive();
      onCountsChanged();
      openConversation(result.conversation.publicId);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setActionBusy(false);
    }
  };

  const reject = async (reasonId: string) => {
    if (!selected) return;
    setActionBusy(true);
    try {
      await adminApi.rejectRequest(selected, reasonId);
      setSelected(null);
      setDetail(null);
      await refreshPendingRequests();
      if (filter === 'archive') await refreshArchive();
      onCountsChanged();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setActionBusy(false);
    }
  };

  const togglePin = async (request: RequestListItem) => {
    await adminApi.updatePriority(request.publicId, {
      priority: request.priority,
      pinned: !request.pinned,
    });
    await refreshPendingRequests();
    if (filter === 'archive') await refreshArchive();
  };

  const visible = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    return query
      ? requests.filter((request) =>
          [
            request.alias,
            request.intent,
            request.firstMessageExcerpt,
            request.sourceProject.label,
          ].some((value) => value?.toLocaleLowerCase().includes(query)),
        )
      : requests;
  }, [requests, search]);

  return (
    <section className="inbox-layout">
      <div className={`request-column ${selected ? 'request-column--mobile-hidden' : ''}`}>
        <header className="column-header">
          <div>
            <small>TRANSMISSION QUEUE</small>
            <h1>{filter === 'pending' ? 'Waiting signals' : 'Archive'}</h1>
          </div>
          <Badge tone={requests.length ? 'purple' : 'neutral'}>{requests.length} total</Badge>
        </header>
        <div className="inbox-toolbar">
          <div className="filter-toggle" role="group" aria-label="Request status filter">
            <button
              className={filter === 'pending' ? 'active' : ''}
              onClick={() => setFilter('pending')}
              aria-pressed={filter === 'pending'}
            >
              Pending
            </button>
            <button
              className={filter === 'archive' ? 'active' : ''}
              onClick={() => setFilter('archive')}
              aria-pressed={filter === 'archive'}
            >
              Archive
            </button>
          </div>
          <label className="search-box">
            <Search />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search signals"
              aria-label="Search requests"
            />
          </label>
        </div>
        <div className="request-list">
          {visible.length === 0 ? (
            <EmptyState
              icon={<Signal />}
              title="No signals here"
              copy={
                filter === 'pending'
                  ? 'The threshold is quiet. Suspicious, but peaceful.'
                  : 'No archived requests match this view.'
              }
            />
          ) : null}
          {visible.map((request) => (
            <article
              key={request.publicId}
              className={`request-card ${selected === request.publicId ? 'selected' : ''} urgency-${request.urgency}`}
            >
              <button
                className="request-card__main"
                onClick={() => void selectRequest(request.publicId)}
              >
                <span className="request-avatar">{request.alias.slice(0, 1).toUpperCase()}</span>
                <span className="request-copy">
                  <span>
                    <strong>{request.alias}</strong>
                    {request.returningVisitor ? <Badge tone="cyan">Returning</Badge> : null}
                  </span>
                  <b>{request.intent ?? 'No intent selected'}</b>
                  <p>{request.firstMessageExcerpt}</p>
                  <small>
                    <Clock3 /> {relativeTime(request.waitingSince)} waiting <i /> #
                    {request.actualQueuePosition} actual <i /> {request.messageCount} message
                    {request.messageCount === 1 ? '' : 's'}
                  </small>
                </span>
                <span className="request-meta">
                  <Badge
                    tone={
                      request.urgency === 'important'
                        ? 'danger'
                        : request.urgency === 'casual'
                          ? 'neutral'
                          : 'purple'
                    }
                  >
                    {request.urgency}
                  </Badge>
                  <small>{request.sourceProject.label}</small>
                </span>
              </button>
              <button
                className="pin-button"
                onClick={() => void togglePin(request)}
                aria-label={request.pinned ? 'Unpin request' : 'Pin request'}
              >
                {request.pinned ? <Pin /> : <PinOff />}
              </button>
            </article>
          ))}
        </div>
      </div>
      <div
        className={`request-detail-column ${selected ? 'request-detail-column--mobile-visible' : ''}`}
      >
        {!selected ? (
          <EmptyState
            icon={<Eye />}
            title="Preview a transmission"
            copy="Open a signal to inspect its messages, context, and safe attachment metadata."
          />
        ) : null}
        {loadingDetail ? (
          <div className="detail-loading">
            <LoaderCircle /> Reading signal…
          </div>
        ) : null}
        {detail && !loadingDetail ? (
          <RequestDetailPane
            detail={detail}
            presets={presets}
            actionBusy={actionBusy}
            localAi={localAi}
            onBack={() => {
              setSelected(null);
              setDetail(null);
            }}
            onAccept={() => void accept()}
            onReject={(reasonId) => void reject(reasonId)}
          />
        ) : null}
        {error ? (
          <div className="panel-error">
            <CircleAlert /> {error}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function RequestDetailPane({
  detail,
  presets,
  actionBusy,
  localAi,
  onBack,
  onAccept,
  onReject,
}: {
  detail: RequestDetail;
  presets: Array<{ id: string; label: string; description: string }>;
  actionBusy: boolean;
  localAi: LocalAiHealth;
  onBack: () => void;
  onAccept: () => void;
  onReject: (reasonId: string) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  return (
    <div className="request-detail">
      <header>
        <button className="detail-back" onClick={onBack} aria-label="Back to requests">
          <X />
        </button>
        <span className="detail-avatar">{detail.alias.slice(0, 1).toUpperCase()}</span>
        <div>
          <span>
            <h2>{detail.alias}</h2>
            {detail.identity.returningVisitor ? <Badge tone="cyan">Returning</Badge> : null}
          </span>
          <p>
            {detail.intent ?? 'No intent'} · {detail.sourceProject.label}
          </p>
        </div>
        <Badge tone={detail.urgency === 'important' ? 'danger' : 'purple'}>{detail.urgency}</Badge>
      </header>
      <div className="detail-facts">
        <span>
          <small>WAITING</small>
          <strong>{relativeTime(detail.createdAt)}</strong>
        </span>
        <span>
          <small>STATE</small>
          <strong>{detail.state}</strong>
        </span>
        <span>
          <small>IDENTITY</small>
          <strong>{detail.identity.mode}</strong>
        </span>
      </div>
      <LocalSummaryCard detail={detail} localAi={localAi} />
      <section className="detail-messages">
        <h3>
          Pending messages <Badge>{detail.messages.length}</Badge>
        </h3>
        {detail.messages.map((message) => (
          <article key={message.publicId}>
            <header>
              <strong>{detail.alias}</strong>
              <time>{new Date(message.createdAt).toLocaleString()}</time>
            </header>
            <Markdown value={message.bodyMarkdown} />
          </article>
        ))}
      </section>
      {detail.attachments.length ? (
        <section className="attachment-metadata">
          <h3>Private attachments</h3>
          <p>
            Only validated derivatives are accessible. Malware scanning is not configured; treat
            every download as untrusted.
          </p>
          {detail.attachments.map((attachment) => (
            <AdminAttachment key={attachment.publicId} attachment={attachment} />
          ))}
        </section>
      ) : null}
      <footer className="detail-actions">
        <Button
          variant="secondary"
          onClick={() => setRejectOpen((value) => !value)}
          disabled={actionBusy}
          aria-expanded={rejectOpen}
          aria-controls="request-reject-menu"
        >
          <X /> Reject
        </Button>
        <Button onClick={onAccept} busy={actionBusy}>
          <Check /> Accept line
        </Button>
        {rejectOpen ? (
          <div
            id="request-reject-menu"
            className="reject-menu"
            role="dialog"
            aria-label="Choose a rejection response"
          >
            <strong>Choose a preset response</strong>
            {presets.map((preset) => (
              <button key={preset.id} onClick={() => onReject(preset.id)}>
                <span>
                  <b>{preset.label}</b>
                  <small>{preset.description}</small>
                </span>
              </button>
            ))}
          </div>
        ) : null}
      </footer>
    </div>
  );
}

function AdminAttachment({
  attachment,
}: {
  attachment: AttachmentView | RequestDetail['attachments'][number];
}) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [speed, setSpeed] = useState(1);
  const audio = useRef<HTMLAudioElement>(null);
  const [transcriptReported, setTranscriptReported] = useState(
    Boolean(attachment.transcript?.errorReportedAt),
  );

  useEffect(() => {
    if (attachment.validationState !== 'validated' || attachment.kind === 'document') return;
    let active = true;
    let previewObjectUrl: string | null = null;
    let mediaObjectUrl: string | null = null;
    const preview = ['image', 'video'].includes(attachment.kind)
      ? adminApi.attachmentBlob(attachment.publicId, true)
      : Promise.resolve(null);
    const media = ['video', 'audio', 'voice'].includes(attachment.kind)
      ? adminApi.attachmentBlob(attachment.publicId)
      : Promise.resolve(null);
    void Promise.all([preview, media])
      .then(([previewBlob, mediaBlob]) => {
        if (!active) return;
        if (previewBlob) {
          previewObjectUrl = URL.createObjectURL(previewBlob);
          setPreviewUrl(previewObjectUrl);
        }
        if (mediaBlob) {
          mediaObjectUrl = URL.createObjectURL(mediaBlob);
          setMediaUrl(mediaObjectUrl);
        }
      })
      .catch((caught) => {
        if (active) setError(errorText(caught));
      });
    return () => {
      active = false;
      if (previewObjectUrl) URL.revokeObjectURL(previewObjectUrl);
      if (mediaObjectUrl) URL.revokeObjectURL(mediaObjectUrl);
    };
  }, [attachment.kind, attachment.publicId, attachment.validationState]);

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      const blob = await adminApi.attachmentBlob(attachment.publicId);
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = attachment.filename;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const reportTranscript = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.reportTranscriptError(attachment.publicId);
      setTranscriptReported(true);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  return (
    <article className="admin-attachment">
      {attachment.kind === 'image' && previewUrl ? (
        <img src={previewUrl} alt={attachment.filename} />
      ) : null}
      {attachment.kind === 'video' && mediaUrl ? (
        <video
          className="admin-attachment__media"
          controls
          preload="metadata"
          poster={previewUrl ?? undefined}
          src={mediaUrl}
        >
          This browser cannot play the safe MP4 derivative.
        </video>
      ) : null}
      {(attachment.kind === 'audio' || attachment.kind === 'voice') && mediaUrl ? (
        <section className="admin-attachment__audio">
          <div className="admin-attachment__waveform" aria-hidden="true">
            {(attachment.waveform ?? Array.from({ length: 32 }, () => 0)).map((peak, index) => (
              <i key={index} style={{ height: `${Math.max(8, peak * 100)}%` }} />
            ))}
          </div>
          <audio ref={audio} controls preload="metadata" src={mediaUrl}>
            This browser cannot play the safe WAV derivative.
          </audio>
          <label>
            Speed
            <select
              value={speed}
              onChange={(event) => {
                const next = Number(event.target.value);
                setSpeed(next);
                if (audio.current) audio.current.playbackRate = next;
              }}
            >
              {[0.75, 1, 1.25, 1.5, 2].map((value) => (
                <option key={value} value={value}>
                  {value}×
                </option>
              ))}
            </select>
          </label>
        </section>
      ) : null}
      <div>
        <span>
          {attachment.kind === 'document' ? (
            <FileText />
          ) : attachment.kind === 'video' ? (
            <Video />
          ) : attachment.kind === 'audio' || attachment.kind === 'voice' ? (
            <AudioLines />
          ) : (
            <Paperclip />
          )}
          <strong>{attachment.filename}</strong>
        </span>
        <small>
          {attachment.mime} · {(attachment.sizeBytes / 1024).toFixed(1)} KB
          {attachment.width && attachment.height
            ? ` · ${attachment.width}×${attachment.height}`
            : ''}
          {attachment.durationMs ? ` · ${(attachment.durationMs / 1_000).toFixed(1)}s` : ''}
        </small>
        <small>
          {attachment.sanitizationState} · {attachment.scanState.replace('_', ' ')}
        </small>
        {'processingErrorCode' in attachment && attachment.processingErrorCode ? (
          <small>{attachment.processingErrorCode}</small>
        ) : null}
        {error ? <small className="admin-attachment__error">{error}</small> : null}
        {attachment.kind === 'voice' ? (
          <div className="admin-attachment__transcript">
            <b>Transcript · {attachment.transcript?.state ?? 'unavailable'}</b>
            <p>
              {attachment.transcript?.state === 'ready' && attachment.transcript.text
                ? attachment.transcript.text
                : attachment.transcript?.state === 'failed'
                  ? 'Transcript processing failed. The audio remains deliverable.'
                  : attachment.transcript?.state === 'pending'
                    ? 'Transcript processing is still pending.'
                    : 'No privacy-preserving transcript is available. The audio remains deliverable.'}
            </p>
            {attachment.transcript?.state === 'ready' ? (
              <button
                type="button"
                disabled={transcriptReported || busy}
                onClick={() => void reportTranscript()}
              >
                {transcriptReported ? 'Issue reported' : 'Report transcript issue'}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
      <Badge tone={attachment.validationState === 'validated' ? 'success' : 'gold'}>
        {attachment.validationState}
      </Badge>
      {attachment.validationState === 'validated' ? (
        <Button variant="ghost" busy={busy} onClick={() => void download()}>
          <Download /> Safe copy
        </Button>
      ) : null}
    </article>
  );
}

function LocalSummaryCard({ detail, localAi }: { detail: RequestDetail; localAi: LocalAiHealth }) {
  const [summary, setSummary] = useState<LocalSummary | null>(() =>
    cachedLocalSummary(detail.publicId),
  );
  const [state, setState] = useState<'idle' | 'loading' | 'ready' | 'unavailable' | 'error'>(() =>
    summary ? 'ready' : 'idle',
  );
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async () => {
    if (localAi.state !== 'ready') {
      setState('unavailable');
      setError(localAi.detail);
      return;
    }
    setState('loading');
    setError(null);
    try {
      const result = await summarizeRequest({
        requestPublicId: detail.publicId,
        alias: detail.alias,
        selectedIntent: detail.intent,
        selectedUrgency: detail.urgency,
        messages: detail.messages.map((message) => message.bodyMarkdown),
      });
      setSummary(result);
      setState('ready');
    } catch (caught) {
      setSummary(null);
      setState('error');
      setError(
        caught instanceof Error
          ? caught.message
          : 'The local model could not organize this request.',
      );
    }
  }, [detail, localAi.detail, localAi.state]);

  useEffect(() => {
    const cached = cachedLocalSummary(detail.publicId);
    setSummary(cached);
    setError(null);
    if (cached) {
      setState('ready');
    } else if (localAi.state === 'ready') {
      void generate();
    } else {
      setState('unavailable');
      setError(localAi.detail);
    }
  }, [detail.publicId, generate, localAi.detail, localAi.state]);

  const retry = () => {
    clearLocalSummary(detail.publicId);
    setSummary(null);
    void generate();
  };

  return (
    <section className="summary-card" aria-live="polite" aria-busy={state === 'loading'}>
      <header>
        <span>
          <Sparkles /> LOCAL SUMMARY
        </span>
        <Badge
          tone={
            state === 'ready'
              ? 'success'
              : state === 'error'
                ? 'danger'
                : state === 'loading'
                  ? 'cyan'
                  : 'neutral'
          }
        >
          {state}
        </Badge>
      </header>
      {summary ? (
        <div className="summary-card__result">
          <p>{summary.summary}</p>
          <dl>
            <div>
              <dt>Tone</dt>
              <dd>{summary.tone}</dd>
            </div>
            <div>
              <dt>Intent</dt>
              <dd>{summary.intent}</dd>
            </div>
            <div>
              <dt>Urgency</dt>
              <dd>{summary.urgency}</dd>
            </div>
          </dl>
        </div>
      ) : (
        <p>
          {state === 'loading'
            ? 'Organizing this request on your device…'
            : (error ??
              'Original messages remain primary. Local summaries appear only while Ollama is ready.')}
        </p>
      )}
      <footer>
        <small>Original messages remain primary. No cloud fallback.</small>
        <Button
          type="button"
          variant="ghost"
          disabled={state === 'loading' || localAi.state !== 'ready'}
          onClick={retry}
        >
          <RefreshCw /> {summary ? 'Regenerate' : 'Retry'}
        </Button>
      </footer>
    </section>
  );
}

function ConversationsView({ initialPublicId }: { initialPublicId: string | null }) {
  const [items, setItems] = useState<ConversationListItem[]>([]);
  const [selected, setSelected] = useState<string | null>(initialPublicId);
  const [conversation, setConversation] = useState<AdminConversation | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [transport, setTransport] = useState<RealtimeTransportState>('idle');
  const [visitorTyping, setVisitorTyping] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);
  const realtime = useRef<ConversationRealtimeClient | null>(null);
  const typingExpiry = useRef<number | null>(null);

  const refreshList = useCallback(async () => {
    try {
      const result = await adminApi.conversations();
      setItems(result.conversations);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);

  const refreshConversation = useCallback(async () => {
    if (!selected) return;
    try {
      const result = await adminApi.conversation(selected);
      setConversation(result.conversation);
      if (result.conversation.unreadCount > 0) {
        const observed = await adminApi.observeConversation(selected);
        if (observed.updatedCount > 0) void refreshList();
      }
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, [refreshList, selected]);

  const handleRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      if (event.type === 'message.created' || event.type === 'signal.created') {
        const incoming = event.payload.message;
        setConversation((current) => {
          if (!current || current.messages.some((item) => item.publicId === incoming.publicId)) {
            return current;
          }
          return {
            ...current,
            unreadCount:
              event.type === 'message.created' && incoming.sender === 'visitor'
                ? current.unreadCount + 1
                : current.unreadCount,
            messages: [...current.messages, incoming],
          };
        });
        if (event.type === 'message.created' && incoming.sender === 'visitor' && selected) {
          void adminApi
            .observeConversation(selected)
            .then((result) => {
              if (result.updatedCount > 0) void refreshList();
            })
            .catch((caught: unknown) => setError(errorText(caught)));
        }
      } else if (event.type === 'message.state_changed') {
        setConversation((current) =>
          current
            ? {
                ...current,
                messages: current.messages.map((item) =>
                  item.publicId === event.payload.messagePublicId
                    ? { ...item, state: event.payload.state }
                    : item,
                ),
              }
            : current,
        );
      } else if (event.type === 'conversation.paused') {
        setConversation((current) =>
          current
            ? {
                ...current,
                state: 'paused',
                pausedReason: event.payload.reason,
                expectedReturn: event.payload.expectedReturn,
              }
            : current,
        );
      } else if (event.type === 'conversation.resumed') {
        setConversation((current) =>
          current
            ? {
                ...current,
                state: 'active',
                pausedReason: null,
                expectedReturn: null,
              }
            : current,
        );
      } else if (event.type === 'typing.started' && event.payload.actor === 'visitor') {
        setVisitorTyping(true);
        if (typingExpiry.current !== null) window.clearTimeout(typingExpiry.current);
        typingExpiry.current = window.setTimeout(
          () => setVisitorTyping(false),
          Math.max(0, Date.parse(event.payload.expiresAt) - Date.now()),
        );
      } else if (event.type === 'typing.stopped' && event.payload.actor === 'visitor') {
        setVisitorTyping(false);
        if (typingExpiry.current !== null) window.clearTimeout(typingExpiry.current);
        typingExpiry.current = null;
      } else if (event.type === 'error' && event.payload.retryable) {
        void refreshConversation();
      }
    },
    [refreshConversation, refreshList, selected],
  );

  useEffect(() => {
    void refreshList();
    const interval = window.setInterval(() => void refreshList(), 5_000);
    return () => window.clearInterval(interval);
  }, [refreshList]);
  useEffect(() => {
    if (!selected && items[0]) setSelected(items[0].publicId);
  }, [items, selected]);
  useEffect(() => {
    void refreshConversation();
    const interval = window.setInterval(
      () => void refreshConversation(),
      transport === 'realtime' ? 15_000 : 3_000,
    );
    return () => window.clearInterval(interval);
  }, [refreshConversation, transport]);
  useEffect(() => {
    if (!selected) return;
    const client = new ConversationRealtimeClient({
      apiBaseUrl: API_BASE_URL,
      conversationPublicId: selected,
      ticketProvider: async () => (await adminApi.realtimeTicket(selected)).realtime,
      onEvent: handleRealtimeEvent,
      onState: setTransport,
    });
    realtime.current = client;
    client.start();
    return () => {
      client.stop();
      realtime.current = null;
      setVisitorTyping(false);
      if (typingExpiry.current !== null) window.clearTimeout(typingExpiry.current);
      typingExpiry.current = null;
    };
  }, [handleRealtimeEvent, selected]);
  useEffect(
    () => bottom.current?.scrollIntoView({ block: 'end' }),
    [conversation?.messages.length],
  );

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!selected || !message.trim()) return;
    setSending(true);
    try {
      await adminApi.sendMessage(selected, message.trim(), crypto.randomUUID());
      realtime.current?.setTyping(false);
      setMessage('');
      await refreshConversation();
      await refreshList();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSending(false);
    }
  };

  const signal = async (
    type: 'seen' | 'hold_on' | 'running_out_of_time' | 'patience_running_out',
  ) => {
    if (!selected) return;
    await adminApi.sendSignal(selected, type);
    await refreshConversation();
  };

  const togglePause = async () => {
    if (!selected || !conversation) return;
    await adminApi.pauseConversation(
      selected,
      conversation.state === 'paused'
        ? { paused: false }
        : { paused: true, reason: 'Building', expectedReturn: null },
    );
    await refreshConversation();
  };

  return (
    <section className="conversation-layout">
      <aside className={`conversation-list ${selected ? 'conversation-list--mobile-hidden' : ''}`}>
        <header className="column-header">
          <div>
            <small>ACCEPTED LINES</small>
            <h1>Conversations</h1>
          </div>
          <Badge tone="cyan">{items.length}</Badge>
        </header>
        <div>
          {items.length === 0 ? (
            <EmptyState
              icon={<MessageCircle />}
              title="No open lines"
              copy="Accepted requests become persistent conversations here."
            />
          ) : (
            items.map((item) => (
              <button
                key={item.publicId}
                className={selected === item.publicId ? 'selected' : ''}
                onClick={() => {
                  setSelected(item.publicId);
                  setConversation(null);
                }}
              >
                <span className="conversation-list-avatar">
                  {item.alias.slice(0, 1).toUpperCase()}
                  <i className={item.state} />
                </span>
                <span>
                  <strong>{item.alias}</strong>
                  <p>{item.lastMessageExcerpt ?? 'Direct line opened'}</p>
                  <small>{relativeTime(item.lastMessageAt)} ago</small>
                </span>
                {item.unreadCount ? <b>{item.unreadCount}</b> : null}
              </button>
            ))
          )}
        </div>
      </aside>
      <div className={`admin-thread ${selected ? 'admin-thread--mobile-visible' : ''}`}>
        {!selected ? (
          <EmptyState
            icon={<MessageCircle />}
            title="Choose a direct line"
            copy="Messages, signals, pause state, and visitor context live here."
          />
        ) : null}
        {selected && !conversation ? (
          <div className="detail-loading">
            <LoaderCircle /> Opening conversation…
          </div>
        ) : null}
        {conversation ? (
          <>
            <header className="thread-header">
              <button
                onClick={() => {
                  setSelected(null);
                  setConversation(null);
                }}
                aria-label="Back"
              >
                <X />
              </button>
              <span className="thread-avatar">{conversation.alias.slice(0, 1).toUpperCase()}</span>
              <div>
                <h2>{conversation.alias}</h2>
                <span>
                  {conversation.intent ?? 'Direct conversation'} · {conversation.state} ·{' '}
                  {transport === 'realtime'
                    ? 'realtime'
                    : transport === 'fallback'
                      ? 'polling backup'
                      : 'connecting'}
                </span>
              </div>
              <Button variant="ghost" onClick={() => void togglePause()}>
                {conversation.state === 'paused' ? <Radio /> : <Pause />}
                {conversation.state === 'paused' ? 'Resume' : 'Pause'}
              </Button>
            </header>
            <div
              className={`thread-pause ${conversation.state === 'paused' ? '' : 'thread-pause--empty'}`}
            >
              {conversation.state === 'paused' ? (
                <>
                  <Pause /> {conversation.pausedReason ?? 'Conversation paused'}
                </>
              ) : null}
            </div>
            <div className="thread-messages" role="log" aria-live="polite">
              {conversation.messages.map((item) => (
                <article
                  key={item.publicId}
                  className={`thread-message thread-message--${item.sender} ${item.type === 'signal' ? 'thread-message--signal' : ''}`}
                >
                  <span>
                    {item.sender === 'visitor'
                      ? conversation.alias.slice(0, 1).toUpperCase()
                      : item.sender === 'admin'
                        ? 'Ð'
                        : '✦'}
                  </span>
                  <div>
                    <header>
                      <strong>
                        {item.sender === 'visitor'
                          ? conversation.alias
                          : item.sender === 'admin'
                            ? 'Ðeffy'
                            : 'Direct Line'}
                      </strong>
                      <time>
                        {new Date(item.createdAt).toLocaleTimeString([], {
                          hour: 'numeric',
                          minute: '2-digit',
                        })}
                      </time>
                    </header>
                    <Markdown value={item.bodyMarkdown} />
                    {item.attachments?.map((attachment) => (
                      <AdminAttachment key={attachment.publicId} attachment={attachment} />
                    ))}
                  </div>
                </article>
              ))}
              <div ref={bottom} />
            </div>
            <div className="signal-bar">
              <span>Signals:</span>
              <button onClick={() => void signal('seen')}>
                <Eye /> Seen
              </button>
              <button onClick={() => void signal('hold_on')}>
                <Clock3 /> Hold On
              </button>
              <button onClick={() => void signal('running_out_of_time')}>
                <Activity /> Time
              </button>
              <button onClick={() => void signal('patience_running_out')}>
                <CircleAlert /> Patience
              </button>
            </div>
            <div className="admin-typing-status" aria-live="polite">
              {visitorTyping ? `${conversation.alias} is typing…` : '\u00a0'}
            </div>
            <form className="admin-composer" onSubmit={send}>
              <textarea
                value={message}
                onChange={(event) => {
                  setMessage(event.target.value);
                  realtime.current?.setTyping(event.target.value.trim().length > 0);
                }}
                onBlur={() => realtime.current?.setTyping(false)}
                rows={2}
                maxLength={10_000}
                placeholder={`Message ${conversation.alias}…`}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    event.currentTarget.form?.requestSubmit();
                  }
                }}
              />
              <Button type="submit" busy={sending} disabled={!message.trim()}>
                <Send />
              </Button>
            </form>
          </>
        ) : null}
        {error ? (
          <div className="panel-error">
            <CircleAlert /> {error}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function BugsView() {
  const [reports, setReports] = useState<BugReportItem[]>([]);
  const [conversationOptions, setConversationOptions] = useState<ConversationListItem[]>([]);
  const [selected, setSelected] = useState<BugReportItem | null>(null);
  const [filter, setFilter] = useState<'open' | 'all' | 'closed'>('open');
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadReports = useCallback(async () => {
    try {
      const [result, conversations] = await Promise.all([
        adminApi.bugReports(),
        adminApi.conversations(),
      ]);
      setReports(result.reports);
      setConversationOptions(conversations.conversations);
      setSelected((current) =>
        current
          ? (result.reports.find((report) => report.publicId === current.publicId) ?? null)
          : null,
      );
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);
  useEffect(() => {
    void loadReports();
  }, [loadReports]);

  const openStatuses = new Set<BugReportItem['status']>(['new', 'triaged', 'in_progress']);
  const openCount = reports.filter((report) => openStatuses.has(report.status)).length;
  const visibleReports = reports.filter((report) =>
    filter === 'all'
      ? true
      : filter === 'open'
        ? openStatuses.has(report.status)
        : report.status === 'resolved' || report.status === 'closed',
  );

  const updateStatus = async (status: BugReportItem['status']) => {
    if (!selected || status === selected.status) return;
    if (
      status === 'closed' &&
      selected.diagnostics.available &&
      !window.confirm(
        'Close this report and permanently delete its retained diagnostics? The structured report remains.',
      )
    ) {
      return;
    }
    setUpdating(true);
    setError(null);
    try {
      const result = await adminApi.updateBugReport(selected.publicId, { status });
      const updated: BugReportItem = {
        ...selected,
        status: result.report.status,
        updatedAt: result.report.updatedAt,
        diagnostics: result.report.diagnosticsDeleted
          ? { basic: null, detailed: null, expiresAt: null, available: false }
          : selected.diagnostics,
      };
      setReports((current) =>
        current.map((report) => (report.publicId === updated.publicId ? updated : report)),
      );
      setSelected(filter === 'open' && !openStatuses.has(updated.status) ? null : updated);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setUpdating(false);
    }
  };

  const updateConversationLink = async (linkedConversationPublicId: string | null) => {
    if (!selected || linkedConversationPublicId === selected.linkedConversationPublicId) return;
    setUpdating(true);
    setError(null);
    try {
      const result = await adminApi.updateBugReport(selected.publicId, {
        linkedConversationPublicId,
      });
      const updated: BugReportItem = {
        ...selected,
        linkedConversationPublicId:
          result.report.linkedConversationPublicId ?? linkedConversationPublicId,
        updatedAt: result.report.updatedAt,
      };
      setReports((current) =>
        current.map((report) => (report.publicId === updated.publicId ? updated : report)),
      );
      setSelected(updated);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <section className="bugs-view">
      <header className="page-heading">
        <div>
          <small>STRUCTURED REPORTS</small>
          <h1>Bug signals</h1>
          <p>Reports stay structured until you choose to turn one into a conversation.</p>
        </div>
        <div className="bug-heading-actions">
          <div className="bug-filters" role="group" aria-label="Filter bug reports">
            {(['open', 'all', 'closed'] as const).map((value) => (
              <button
                key={value}
                className={filter === value ? 'selected' : ''}
                onClick={() => {
                  setFilter(value);
                  setSelected(null);
                }}
              >
                {value}
              </button>
            ))}
          </div>
          <Button variant="ghost" onClick={() => void loadReports()} aria-label="Refresh reports">
            <RefreshCw />
          </Button>
          <Badge tone="gold">{openCount} open</Badge>
        </div>
      </header>
      <div className="bugs-grid">
        <div className="bug-list">
          {visibleReports.length === 0 ? (
            <EmptyState
              icon={<Bug />}
              title="No bug reports"
              copy="Either everything works or nobody has found the cursed button yet."
            />
          ) : (
            visibleReports.map((report) => (
              <button
                key={report.publicId}
                className={selected?.publicId === report.publicId ? 'selected' : ''}
                onClick={() => setSelected(report)}
              >
                <span className={`severity-dot severity-${report.severity}`} />
                <span>
                  <strong>{report.project.label}</strong>
                  <p>{report.description}</p>
                  <small>
                    {relativeTime(report.createdAt)} ago · {report.status}
                  </small>
                </span>
                <Badge
                  tone={
                    report.severity === 'critical' || report.severity === 'high'
                      ? 'danger'
                      : 'neutral'
                  }
                >
                  {report.severity}
                </Badge>
              </button>
            ))
          )}
        </div>
        <div className="bug-detail">
          {!selected ? (
            <EmptyState
              icon={<Bug />}
              title="Choose a report"
              copy="Expected behavior, actual behavior, reproduction, and consent appear here."
            />
          ) : (
            <article>
              <header>
                <span>
                  <Badge tone="gold">{selected.project.label}</Badge>
                  <Badge>{selected.status}</Badge>
                </span>
                <code>{selected.publicId}</code>
              </header>
              <div className="bug-status-control">
                <label>
                  <span>Workflow status</span>
                  <select
                    value={selected.status}
                    disabled={updating}
                    onChange={(event) =>
                      void updateStatus(event.target.value as BugReportItem['status'])
                    }
                  >
                    <option value="new">New</option>
                    <option value="triaged">Triaged</option>
                    <option value="in_progress">In progress</option>
                    <option value="resolved">Resolved</option>
                    <option value="closed">Closed + erase diagnostics</option>
                  </select>
                </label>
                {updating ? <LoaderCircle className="spin" aria-label="Updating status" /> : null}
              </div>
              <div className="bug-status-control">
                <label>
                  <span>Linked conversation</span>
                  <select
                    value={selected.linkedConversationPublicId ?? ''}
                    disabled={updating}
                    onChange={(event) => void updateConversationLink(event.target.value || null)}
                  >
                    <option value="">Keep as a standalone report</option>
                    {conversationOptions.map((conversation) => (
                      <option key={conversation.publicId} value={conversation.publicId}>
                        {conversation.alias} · {conversation.publicId}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
              <h2>{selected.description}</h2>
              <DetailBlock title="Expected behavior" value={selected.expectedBehavior} />
              <DetailBlock title="Actual behavior" value={selected.actualBehavior} />
              <DetailBlock title="Reproduction" value={selected.reproduction} />
              <DetailBlock
                title="Reply preference"
                value={
                  selected.replyPreference === 'conversation'
                    ? 'A conversation is welcome.'
                    : selected.replyPreference === 'if_needed'
                      ? 'Reply only if more detail is needed.'
                      : 'No reply needed.'
                }
              />
              {selected.evidence.length ? (
                <section className="attachment-metadata bug-evidence">
                  <h3>Private evidence</h3>
                  <p>
                    Only validated derivatives are shown. Malware scanning is not configured; treat
                    downloads as untrusted.
                  </p>
                  {selected.evidence.map((attachment) => (
                    <AdminAttachment key={attachment.publicId} attachment={attachment} />
                  ))}
                </section>
              ) : null}
              <div className="diagnostic-consent">
                {selected.diagnosticConsent ? <Check /> : <EyeOff />} Detailed diagnostics{' '}
                {selected.diagnosticConsent ? 'were consented to' : 'were not shared'}
              </div>
              <BugDiagnostics diagnostics={selected.diagnostics} />
            </article>
          )}
        </div>
      </div>
      {error ? (
        <div className="panel-error">
          <CircleAlert /> {error}
        </div>
      ) : null}
    </section>
  );
}

function BugDiagnostics({ diagnostics }: { diagnostics: BugReportItem['diagnostics'] }) {
  if (!diagnostics.available || !diagnostics.basic) {
    return (
      <section className="bug-diagnostics bug-diagnostics--empty">
        <small>DIAGNOSTIC CONTEXT</small>
        <p>Expired, deleted at closure, or unavailable for this report.</p>
      </section>
    );
  }
  const basicEntries = [
    ['Source site', diagnostics.basic.sourceSite],
    ['Source page', diagnostics.basic.currentUrl],
    ['Build', diagnostics.basic.buildVersion],
    ['Browser', diagnostics.basic.browserFamily],
    ['Operating system', diagnostics.basic.operatingSystem],
    ['Viewport', diagnostics.basic.viewportCategory],
    ['Captured', diagnostics.basic.timestamp],
  ];
  const state = diagnostics.detailed?.systemState;
  return (
    <section className="bug-diagnostics">
      <header>
        <small>DIAGNOSTIC CONTEXT</small>
        {diagnostics.expiresAt ? (
          <span>Retained until {new Date(diagnostics.expiresAt).toLocaleDateString()}</span>
        ) : null}
      </header>
      <dl>
        {basicEntries.map(([label, value]) => (
          <div key={label}>
            <dt>{label}</dt>
            <dd>{value || 'Not supplied'}</dd>
          </div>
        ))}
      </dl>
      {state ? (
        <div className="bug-system-state">
          <strong>Consented system state</strong>
          <p>
            {state.online ? 'Online' : 'Offline'} · {state.language} · {state.colorScheme} scheme ·{' '}
            {state.pixelRatioCategory} pixel density · {state.navigationType}
            {state.reducedMotion ? ' · reduced motion' : ''}
          </p>
        </div>
      ) : null}
      <DiagnosticLines title="Console excerpts" lines={diagnostics.detailed?.consoleLogs} />
      <DiagnosticLines
        title="Recent application events"
        lines={diagnostics.detailed?.recentApplicationEvents}
      />
      <DiagnosticLines title="Network errors" lines={diagnostics.detailed?.networkErrors} />
      <DiagnosticLines title="Reproduction trace" lines={diagnostics.detailed?.reproductionTrace} />
    </section>
  );
}

function DiagnosticLines({ title, lines }: { title: string; lines: string[] | undefined }) {
  if (!lines?.length) return null;
  return (
    <div className="diagnostic-lines">
      <strong>{title}</strong>
      <ol>
        {lines.map((line, index) => (
          <li key={`${index}-${line.slice(0, 24)}`}>{line}</li>
        ))}
      </ol>
    </div>
  );
}

function DetailBlock({ title, value }: { title: string; value: string | null }) {
  return (
    <section className="detail-block">
      <small>{title}</small>
      <p>{value ?? 'Not provided.'}</p>
    </section>
  );
}

function SignalPortalView() {
  const [config, setConfig] = useState<SignalPortalConfig | null>(null);
  const [status, setStatus] = useState<'online' | 'custom' | 'offline'>('offline');
  const [invisible, setInvisible] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const projectId = 'deffy-direct';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.signalPortalConfig(projectId);
      setConfig(result.config);
      const statusResult = await adminApi.signalPortalStatus();
      setStatus(statusResult.publicStatus as 'online' | 'custom' | 'offline');
      setInvisible(statusResult.invisible);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const update = async (patch: Partial<SignalPortalConfigUpdate>) => {
    if (!config) return;
    setSaving(true);
    setError(null);
    try {
      const result = await adminApi.signalPortalUpdateConfig(projectId, patch);
      setConfig(result.config);
      setSavedAt(Date.now());
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  };

  const updateBranding = (patch: Partial<SignalPortalConfig['branding']>) =>
    update({ branding: { ...config?.branding, ...patch } });

  const updateLauncher = (patch: Partial<SignalPortalConfig['launcher']>) =>
    update({ launcher: { ...config?.launcher, ...patch } });

  const updateBehavior = (patch: Partial<SignalPortalConfig['behavior']>) =>
    update({ behavior: { ...config?.behavior, ...patch } });

  const updateStatus = async (newStatus: 'online' | 'custom' | 'offline', newInvisible?: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await adminApi.signalPortalUpdateStatus({
        publicStatus: newStatus,
        ...(newInvisible !== undefined ? { invisible: newInvisible } : {}),
      });
      setStatus(newStatus);
      if (newInvisible !== undefined) setInvisible(newInvisible);
      setSavedAt(Date.now());
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-loading">
        <LoaderCircle /> Loading Signal Portal configuration…
      </div>
    );
  }

  if (!config) {
    return (
      <EmptyState
        icon={<Signal />}
        title="No configuration"
        copy="Signal Portal is not configured for this project."
      />
    );
  }

  return (
    <section className="health-panel signal-portal-config">
      <header>
        <span>
          <Signal /> Signal Portal Configuration
        </span>
        <Badge tone={config.enabled ? 'success' : 'neutral'}>
          {config.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
      </header>

      {error ? <div className="dashboard-warning"><CircleAlert /> {error}</div> : null}
      {savedAt ? (
        <div className="signal-portal-saved">
          <Check /> Saved {relativeTime(new Date(savedAt).toISOString())}
        </div>
      ) : null}

      <div className="signal-portal-section">
        <h3>Availability</h3>
        <label className="signal-portal-field">
          <span>Public status</span>
          <select
            value={status}
            onChange={(e) => void updateStatus(e.target.value as 'online' | 'custom' | 'offline')}
            disabled={saving}
          >
            <option value="online">Online</option>
            <option value="custom">Custom</option>
            <option value="offline">Offline</option>
          </select>
        </label>
        <label className="signal-portal-field">
          <span>Invisible (shows offline to visitors)</span>
          <input
            type="checkbox"
            checked={invisible}
            onChange={(e) => void updateStatus(status, e.target.checked)}
            disabled={saving}
          />
        </label>
      </div>

      <div className="signal-portal-section">
        <h3>General</h3>
        <label className="signal-portal-field">
          <span>Enabled</span>
          <input
            type="checkbox"
            checked={config.enabled}
            onChange={(e) => void update({ enabled: e.target.checked })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Project label</span>
          <input
            type="text"
            value={config.branding.projectLabel}
            onChange={(e) => updateBranding({ projectLabel: e.target.value })}
            disabled={saving}
            maxLength={60}
          />
        </label>
        <label className="signal-portal-field">
          <span>Accent color</span>
          <input
            type="color"
            value={config.branding.accent}
            onChange={(e) => updateBranding({ accent: e.target.value })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Preset</span>
          <select
            value={config.branding.presetId}
            onChange={(e) => updateBranding({ presetId: e.target.value as SignalPortalConfig['branding']['presetId'] })}
            disabled={saving}
          >
            <option value="signal-neutral-dark">Neutral Dark</option>
            <option value="signal-neutral-light">Neutral Light</option>
            <option value="cosmotech">Cosmotech</option>
            <option value="corrupture">Corrupture</option>
            <option value="project-branded">Project Branded</option>
          </select>
        </label>
      </div>

      <div className="signal-portal-section">
        <h3>Greetings</h3>
        <label className="signal-portal-field">
          <span>Online greeting</span>
          <input
            type="text"
            value={config.branding.greetingOnline}
            onChange={(e) => updateBranding({ greetingOnline: e.target.value })}
            disabled={saving}
            maxLength={120}
          />
        </label>
        <label className="signal-portal-field">
          <span>Offline greeting</span>
          <input
            type="text"
            value={config.branding.greetingOffline}
            onChange={(e) => updateBranding({ greetingOffline: e.target.value })}
            disabled={saving}
            maxLength={120}
          />
        </label>
        <label className="signal-portal-field">
          <span>Purpose text</span>
          <textarea
            value={config.branding.purposeText}
            onChange={(e) => updateBranding({ purposeText: e.target.value })}
            disabled={saving}
            maxLength={200}
            rows={2}
          />
        </label>
      </div>

      <div className="signal-portal-section">
        <h3>Launcher</h3>
        <label className="signal-portal-field">
          <span>Default side</span>
          <select
            value={config.launcher.defaultSide}
            onChange={(e) => updateLauncher({ defaultSide: e.target.value as 'left' | 'right' })}
            disabled={saving}
          >
            <option value="left">Left</option>
            <option value="right">Right</option>
          </select>
        </label>
        <label className="signal-portal-field">
          <span>Vertical position (%)</span>
          <input
            type="number"
            min={0}
            max={100}
            value={config.launcher.defaultVerticalPercent}
            onChange={(e) => updateLauncher({ defaultVerticalPercent: Number(e.target.value) })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Pulse animation</span>
          <input
            type="checkbox"
            checked={config.launcher.pulseEnabled}
            onChange={(e) => updateLauncher({ pulseEnabled: e.target.checked })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Proactive greeting</span>
          <input
            type="checkbox"
            checked={config.launcher.proactiveEnabled}
            onChange={(e) => updateLauncher({ proactiveEnabled: e.target.checked })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Sound enabled by default</span>
          <input
            type="checkbox"
            checked={config.launcher.soundEnabledByDefault}
            onChange={(e) => updateLauncher({ soundEnabledByDefault: e.target.checked })}
            disabled={saving}
          />
        </label>
      </div>

      <div className="signal-portal-section">
        <h3>Proactive Behavior</h3>
        <label className="signal-portal-field">
          <span>Min delay (seconds)</span>
          <input
            type="number"
            min={5}
            max={300}
            value={config.behavior.proactiveMinSeconds}
            onChange={(e) => updateBehavior({ proactiveMinSeconds: Number(e.target.value) })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Max delay (seconds)</span>
          <input
            type="number"
            min={30}
            max={600}
            value={config.behavior.proactiveMaxSeconds}
            onChange={(e) => updateBehavior({ proactiveMaxSeconds: Number(e.target.value) })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Session cap</span>
          <input
            type="number"
            min={1}
            max={10}
            value={config.behavior.proactiveSessionCap}
            onChange={(e) => updateBehavior({ proactiveSessionCap: Number(e.target.value) })}
            disabled={saving}
          />
        </label>
        <label className="signal-portal-field">
          <span>Cross-day cooldown (hours)</span>
          <input
            type="number"
            min={1}
            max={168}
            value={config.behavior.proactiveCrossDayCooldownHours}
            onChange={(e) => updateBehavior({ proactiveCrossDayCooldownHours: Number(e.target.value) })}
            disabled={saving}
          />
        </label>
      </div>

      <div className="signal-portal-section">
        <h3>Live Preview</h3>
        <div className="signal-portal-preview" style={{ '--sp-accent': config.branding.accent } as React.CSSProperties}>
          <div className="signal-portal-preview-launcher" style={{ background: config.branding.accent }}>
            <Signal size={20} color="#fff" />
          </div>
          <div className="signal-portal-preview-panel">
            <div className="signal-portal-preview-header" style={{ borderBottom: `2px solid ${config.branding.accent}` }}>
              <span style={{ color: config.branding.accent }}>●</span>
              <strong>{config.branding.projectLabel}</strong>
            </div>
            <div className="signal-portal-preview-body">
              <p>{config.branding.greetingOnline}</p>
              <p className="signal-portal-preview-purpose">{config.branding.purposeText}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SystemView({
  overview,
  localAi,
  onLocalAiSettingsChanged,
  notificationSettings,
  onNotificationSettingsChanged,
}: {
  overview: Overview | null;
  localAi: LocalAiHealth;
  onLocalAiSettingsChanged: () => void;
  notificationSettings: AdminNotificationSettings;
  onNotificationSettingsChanged: (settings: AdminNotificationSettings) => void;
}) {
  const [health, setHealth] = useState<{
    status: string;
    checks: Record<string, string>;
    checkedAt: string;
    durationMs: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const refresh = useCallback(async () => {
    try {
      setHealth(await adminApi.health());
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const cards = overview
    ? ([
        ['Pending requests', overview.counts.pendingRequests, <Inbox key="inbox" />],
        [
          'Active conversations',
          overview.counts.activeConversations,
          <MessageCircle key="messages" />,
        ],
        ['Unread messages', overview.counts.unreadMessages, <Bell key="bell" />],
        ['Quarantined files', overview.counts.quarantinedAttachments, <Shield key="shield" />],
      ] as const)
    : [];
  return (
    <section className="system-view">
      <header className="page-heading">
        <div>
          <small>OPERATIONS</small>
          <h1>System node</h1>
          <p>Truthful health signals. Unimplemented services are labeled, not painted green.</p>
        </div>
        <Button
          variant="secondary"
          onClick={() => {
            void refresh();
            onLocalAiSettingsChanged();
          }}
        >
          <RefreshCw /> Refresh
        </Button>
      </header>
      <div className="metric-cards">
        {cards.map(([label, value, icon]) => (
          <div key={label}>
            <span>{icon}</span>
            <strong>{value}</strong>
            <small>{label}</small>
          </div>
        ))}
      </div>
      <div className="system-grid">
        <section className="health-panel">
          <header>
            <span>
              <Server /> Platform checks
            </span>
            <Badge tone={health?.status === 'ok' ? 'success' : 'danger'}>
              {health?.status ?? 'checking'}
            </Badge>
          </header>
          {health ? (
            Object.entries(health.checks).map(([name, state]) => (
              <div key={name}>
                <span>{name.replaceAll('_', ' ')}</span>
                <strong
                  className={
                    state === 'ok' || state === 'configured'
                      ? 'ok'
                      : state.startsWith('not_')
                        ? 'pending'
                        : ''
                  }
                >
                  {state.replaceAll('_', ' ')}
                </strong>
              </div>
            ))
          ) : (
            <div className="detail-loading">
              <LoaderCircle /> Checking…
            </div>
          )}
        </section>
        <LocalAiSettingsPanel health={localAi} onSettingsChanged={onLocalAiSettingsChanged} />
        <AdminNotificationSettingsPanel
          settings={notificationSettings}
          onSettingsChanged={onNotificationSettingsChanged}
        />
        <section className="health-panel">
          <header>
            <span>
              <Wifi /> Public presence
            </span>
            <Badge tone="purple">{overview?.presence.label ?? 'loading'}</Badge>
          </header>
          <div>
            <span>Expected reply</span>
            <strong>{overview?.presence.expectedReply ?? '—'}</strong>
          </div>
          <div>
            <span>Privacy mask</span>
            <strong>{overview?.presence.privacyMode ? 'enabled' : 'disabled'}</strong>
          </div>
          <p>{overview?.presence.subtitle ?? overview?.presence.publicMessage}</p>
        </section>
      </div>
      {error ? (
        <div className="panel-error">
          <CircleAlert /> {error}
        </div>
      ) : null}
    </section>
  );
}

function RedLineView() {
  return (
    <section className="red-line-view">
      <header className="page-heading">
        <div>
          <small>EMERGENCY CHANNEL</small>
          <h1>Red Line</h1>
          <p>
            Direct, high-priority line for visitors in urgent need. Open the channel, monitor live
            transcripts, and convert to a standard conversation when the crisis stabilizes.
          </p>
        </div>
      </header>
      <RedLinePanel />
    </section>
  );
}

function LocalAiSettingsPanel({
  health,
  onSettingsChanged,
}: {
  health: LocalAiHealth;
  onSettingsChanged: () => void;
}) {
  const [settings, setSettings] = useState<LocalAiSettings>(health.settings);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setSettings(health.settings), [health.settings]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      setSettings(saveLocalAiSettings(settings));
      onSettingsChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'These settings are invalid.');
    }
  };

  return (
    <section className="health-panel health-panel--local-ai">
      <header>
        <span>
          <Sparkles /> Local organization AI
        </span>
        <Badge
          tone={
            health.state === 'ready'
              ? 'success'
              : health.state === 'offline' || health.state === 'misconfigured'
                ? 'danger'
                : 'gold'
          }
        >
          {health.state.replace('_', ' ')}
        </Badge>
      </header>
      <form onSubmit={submit} className="local-ai-settings">
        <label>
          <span>Loopback endpoint</span>
          <input
            value={settings.endpoint}
            onChange={(event) =>
              setSettings((current) => ({ ...current, endpoint: event.target.value }))
            }
            inputMode="url"
            autoComplete="url"
            required
          />
        </label>
        <label>
          <span>Local model</span>
          <input
            value={settings.model}
            onChange={(event) =>
              setSettings((current) => ({ ...current, model: event.target.value }))
            }
            list="local-ollama-models"
            autoComplete="off"
            required
          />
          <datalist id="local-ollama-models">
            {health.models.map((model) => (
              <option key={model} value={model} />
            ))}
          </datalist>
        </label>
        <Button type="submit" variant="secondary">
          <RefreshCw /> Save &amp; recheck
        </Button>
      </form>
      <p className={error ? 'local-ai-detail local-ai-detail--error' : 'local-ai-detail'}>
        {error ?? health.detail}
      </p>
      {health.state === 'model_missing' ? (
        <p>
          Install it locally with <code>ollama pull {health.settings.model}</code>.
        </p>
      ) : null}
      <p>
        Only loopback servers and non-cloud model names are accepted. If the hosted dashboard is
        blocked by Ollama CORS, allow exactly <code>https://admin.deffy.me</code> in{' '}
        <code>OLLAMA_ORIGINS</code> before starting Ollama.
      </p>
      <p>Summaries run only while this dashboard is open. No cloud fallback is used.</p>
    </section>
  );
}

function AdminNotificationSettingsPanel({
  settings,
  onSettingsChanged,
}: {
  settings: AdminNotificationSettings;
  onSettingsChanged: (settings: AdminNotificationSettings) => void;
}) {
  const [draft, setDraft] = useState(settings);
  const [escalation, setEscalation] = useState(settings.escalationMinutes.join(', '));
  const [permission, setPermission] = useState(() => notificationPermission());
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    setDraft(settings);
    setEscalation(settings.escalationMinutes.join(', '));
  }, [settings]);

  const persist = (next: AdminNotificationSettings) => {
    const saved = saveAdminNotificationSettings(next);
    setDraft(saved);
    onSettingsChanged(saved);
    return saved;
  };

  const enableBrowser = async () => {
    const nextPermission = await requestNotificationPermission();
    setPermission(nextPermission);
    if (nextPermission === 'granted') {
      persist({ ...draft, browserEnabled: true });
      setFeedback('Browser notifications enabled.');
    } else {
      persist({ ...draft, browserEnabled: false });
      setFeedback(
        nextPermission === 'denied'
          ? 'Permission was denied. Change it in browser site settings to enable alerts.'
          : 'This browser does not support notifications.',
      );
    }
  };

  const toggleSound = async () => {
    if (!draft.soundEnabled) {
      const primed = await primeNotificationSound();
      if (!primed) {
        setFeedback('This browser could not start notification audio.');
        return;
      }
    }
    const next = persist({ ...draft, soundEnabled: !draft.soundEnabled });
    setFeedback(next.soundEnabled ? 'Sound enabled.' : 'Sound muted.');
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    try {
      const minutes = escalation
        .split(',')
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value));
      persist({ ...draft, escalationMinutes: minutes });
      setFeedback('Notification settings saved.');
    } catch (caught) {
      setFeedback(caught instanceof Error ? caught.message : 'These settings are invalid.');
    }
  };

  const test = async () => {
    if (draft.soundEnabled) await primeNotificationSound();
    setFeedback(
      sendAdminNotificationTest(draft)
        ? 'Test signal sent.'
        : 'Enable browser notifications or sound before testing.',
    );
  };

  return (
    <section className="health-panel health-panel--notifications">
      <header>
        <span>
          <Bell /> Alerts &amp; escalation
        </span>
        <Badge tone={draft.browserEnabled || draft.soundEnabled ? 'success' : 'neutral'}>
          {draft.browserEnabled || draft.soundEnabled ? 'enabled' : 'muted'}
        </Badge>
      </header>
      <form className="notification-settings" onSubmit={submit}>
        <div className="notification-settings__toggles">
          <Button
            type="button"
            variant={draft.browserEnabled ? 'secondary' : 'ghost'}
            onClick={() => void enableBrowser()}
          >
            <Bell /> Browser {draft.browserEnabled ? 'on' : 'off'}
          </Button>
          <Button
            type="button"
            variant={draft.soundEnabled ? 'secondary' : 'ghost'}
            onClick={() => void toggleSound()}
          >
            {draft.soundEnabled ? <Volume2 /> : <VolumeX />} Sound{' '}
            {draft.soundEnabled ? 'on' : 'off'}
          </Button>
        </div>
        <label>
          <span>Sound</span>
          <select
            value={draft.sound}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                sound: event.target.value as AdminNotificationSettings['sound'],
              }))
            }
          >
            <option value="cosmic_chime">Cosmic chime</option>
            <option value="soft_ping">Soft ping</option>
          </select>
        </label>
        <label>
          <span>Volume · {Math.round(draft.volume * 100)}%</span>
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={draft.volume}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                volume: Number(event.target.value),
              }))
            }
          />
        </label>
        <label>
          <span>Escalate after minutes (comma-separated, max 5)</span>
          <input
            value={escalation}
            onChange={(event) => setEscalation(event.target.value)}
            inputMode="numeric"
            placeholder="5, 15, 30"
          />
        </label>
        <label className="notification-settings__check">
          <input
            type="checkbox"
            checked={draft.reducedSensory}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                reducedSensory: event.target.checked,
                soundEnabled: event.target.checked ? false : current.soundEnabled,
              }))
            }
          />
          <span>Reduced-sensory mode (disables alert motion and sound)</span>
        </label>
        <div className="notification-settings__actions">
          <Button type="submit" variant="secondary">
            Save rules
          </Button>
          <Button type="button" variant="ghost" onClick={() => void test()}>
            Test alert
          </Button>
        </div>
      </form>
      <p>
        Permission: <strong>{permission}</strong>. Alerts fire once per stage and stop escalating
        when a request is acknowledged.
      </p>
      {feedback ? <p className="local-ai-detail">{feedback}</p> : null}
    </section>
  );
}

function EmptyState({ icon, title, copy }: { icon: ReactNode; title: string; copy: string }) {
  return (
    <div className="empty-state">
      <span>{icon}</span>
      <strong>{title}</strong>
      <p>{copy}</p>
    </div>
  );
}

interface RedLineStatus {
  availabilityState: RedLineAvailabilityState;
  sessionState: string | null;
  capacity: number;
  activeCount: number;
  openUntil: string | null;
  adminHeartbeatFresh: boolean;
  lastStateChange: string;
  activeSession: {
    conversationPublicId: string;
    sessionState: string;
    startedAt: string;
    lastActivityAt: string;
    idleWarningSent: boolean;
    priorityPingsUsed: number;
    visitorPresent: boolean;
  } | null;
  blocked: boolean;
}

const DURATION_LABELS: Record<number, string> = {
  [15 * 60]: '15 minutes',
  [30 * 60]: '30 minutes',
  [60 * 60]: '1 hour',
  0: 'Until closed',
};

function RedLinePanel() {
  const [status, setStatus] = useState<RedLineStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [messageBusy, setMessageBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showClosePresets, setShowClosePresets] = useState(false);
  const [messages, setMessages] = useState<RedLineMessageView[]>([]);
  const [messageInput, setMessageInput] = useState('');
  const [transport, setTransport] = useState<RealtimeTransportState>('idle');
  const [visitorTyping, setVisitorTyping] = useState(false);
  const [lastSignal, setLastSignal] = useState<RedLineSignalReaction | null>(null);
  const realtimeRef = useRef<ConversationRealtimeClient | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const conversationId = status?.activeSession?.conversationPublicId ?? null;

  const refresh = useCallback(async () => {
    try {
      setStatus(await adminApi.redLineStatus());
      setError(null);
    } catch (caught) {
      setError(errorText(caught));
    }
  }, []);

  useEffect(() => {
    void refresh();
    const interval = setInterval(() => void refresh(), 5_000);
    return () => clearInterval(interval);
  }, [refresh]);

  const heartbeatActive =
    Boolean(status) &&
    !status?.blocked &&
    status?.availabilityState !== 'CLOSED' &&
    status?.availabilityState !== 'EXPIRED';

  useEffect(() => {
    if (heartbeatActive) {
      const heartbeat = setInterval(() => {
        void adminApi.redLineHeartbeat().catch(() => {});
      }, 30_000);
      return () => clearInterval(heartbeat);
    }
    return undefined;
  }, [heartbeatActive]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, visitorTyping]);

  useEffect(() => {
    if (!lastSignal) return;
    const timer = window.setTimeout(() => setLastSignal(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [lastSignal]);

  const appendMessage = useCallback((message: RedLineMessageView) => {
    setMessages((current) => {
      if (current.some((item) => item.publicId === message.publicId)) return current;
      return [...current, message].sort(
        (left, right) => Date.parse(left.createdAt) - Date.parse(right.createdAt),
      );
    });
  }, []);

  const refreshMessages = useCallback(async () => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    try {
      const result = await adminApi.redLineMessages();
      setMessages(result.messages);
    } catch (caught) {
      if (caught instanceof AdminApiFailure && caught.code === 'RED_LINE_NO_ACTIVE_SESSION') {
        setMessages([]);
        return;
      }
      setError(errorText(caught));
    }
  }, [conversationId]);

  const handleRealtimeEvent = useCallback(
    (event: RealtimeEvent) => {
      switch (event.type) {
        case 'red_line.message_created':
          appendMessage({
            publicId: event.payload.message.publicId,
            sender: event.payload.message.sender,
            bodyMarkdown: event.payload.message.bodyMarkdown,
            emphasis: event.payload.emphasis,
            contextChip: event.payload.contextChip as RedLineMessageView['contextChip'],
            createdAt: event.payload.message.createdAt,
          });
          break;
        case 'typing.started':
          if (event.payload.actor === 'visitor') setVisitorTyping(true);
          break;
        case 'typing.stopped':
          if (event.payload.actor === 'visitor') setVisitorTyping(false);
          break;
        case 'red_line.typing':
          if (event.payload.actor === 'visitor') setVisitorTyping(event.payload.active);
          break;
        case 'red_line.signal':
          if (event.payload.actor === 'visitor') setLastSignal(event.payload.reaction);
          break;
        case 'red_line.presence':
          setStatus((current) =>
            current?.activeSession
              ? {
                  ...current,
                  activeSession: {
                    ...current.activeSession,
                    visitorPresent: event.payload.visitorPresent,
                  },
                }
              : current,
          );
          break;
        case 'red_line.ended':
        case 'red_line.converted':
          setVisitorTyping(false);
          setMessageInput('');
          void refresh();
          break;
        case 'red_line.state_changed':
        case 'red_line.priority_ping':
          void refresh();
          break;
        case 'error':
          if (event.payload.retryable) void refreshMessages();
          else setError(event.payload.message);
          break;
        default:
          break;
      }
    },
    [appendMessage, refresh, refreshMessages],
  );

  useEffect(() => {
    if (!conversationId) {
      realtimeRef.current?.stop();
      realtimeRef.current = null;
      setTransport('idle');
      setVisitorTyping(false);
      return undefined;
    }
    const client = new ConversationRealtimeClient({
      apiBaseUrl: API_BASE_URL,
      conversationPublicId: conversationId,
      ticketProvider: async () => (await adminApi.redLineRealtimeTicket()).realtime,
      onEvent: handleRealtimeEvent,
      onState: setTransport,
    });
    realtimeRef.current = client;
    client.start();
    return () => {
      client.stop();
      if (realtimeRef.current === client) realtimeRef.current = null;
    };
  }, [conversationId, handleRealtimeEvent]);

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return undefined;
    }
    void refreshMessages();
    const interval = window.setInterval(
      () => void refreshMessages(),
      transport === 'realtime' ? 15_000 : 3_000,
    );
    return () => window.clearInterval(interval);
  }, [conversationId, refreshMessages, transport]);

  const open = async (durationSeconds: number) => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineOpen(durationSeconds);
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const close = async (preset?: string) => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineClose(preset);
      setShowClosePresets(false);
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const endSession = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineEndSession();
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const block = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineBlock();
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const unblock = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineUnblock();
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const convert = async () => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineConvert();
      await refresh();
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const sendSignal = async (reaction: RedLineSignalReaction) => {
    setBusy(true);
    setError(null);
    try {
      await adminApi.redLineSignal(reaction);
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async (event: FormEvent) => {
    event.preventDefault();
    const message = messageInput.trim();
    if (!conversationId || !message || messageBusy) return;
    setMessageBusy(true);
    setError(null);
    setMessageInput('');
    realtimeRef.current?.setTyping(false);
    try {
      const result = await adminApi.redLineMessage(message, crypto.randomUUID());
      if (!result.message.duplicate) {
        appendMessage({
          publicId: result.message.publicId,
          sender: 'admin',
          bodyMarkdown: message,
          emphasis: 'NORMAL',
          contextChip: null,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (caught) {
      setMessageInput(message);
      setError(errorText(caught));
    } finally {
      setMessageBusy(false);
    }
  };

  const stateLabel = status ? (status.blocked ? 'BLOCKED' : status.availabilityState) : 'loading';
  const tone =
    stateLabel === 'OPEN'
      ? 'success'
      : stateLabel === 'OCCUPIED'
        ? 'gold'
        : stateLabel === 'BLOCKED'
          ? 'danger'
          : 'neutral';

  return (
    <section className="health-panel red-line-panel">
      <header>
        <span>
          <Radio /> Red Line
        </span>
        <Badge tone={tone}>{stateLabel}</Badge>
      </header>

      {status ? (
        <>
          <div>
            <span>Capacity</span>
            <strong>
              {status.activeCount} / {status.capacity}
            </strong>
          </div>
          <div>
            <span>Admin heartbeat</span>
            <strong className={status.adminHeartbeatFresh ? 'ok' : ''}>
              {status.adminHeartbeatFresh ? 'fresh' : 'stale'}
            </strong>
          </div>
          {status.openUntil ? (
            <div>
              <span>Open until</span>
              <strong>{new Date(status.openUntil).toLocaleTimeString()}</strong>
            </div>
          ) : null}
          {status.activeSession ? (
            <>
              <div className="red-line-active-session">
                <div>
                  <span>Session</span>
                  <strong>{status.activeSession.sessionState}</strong>
                </div>
                <div>
                  <span>Visitor present</span>
                  <strong>{status.activeSession.visitorPresent ? 'yes' : 'no'}</strong>
                </div>
                <div>
                  <span>Priority pings</span>
                  <strong>{status.activeSession.priorityPingsUsed}</strong>
                </div>
                <div>
                  <span>Transport</span>
                  <strong className={transport === 'realtime' ? 'ok' : ''}>{transport}</strong>
                </div>
              </div>

              <div className="red-line-live-console">
                <header>
                  <span>
                    {transport === 'realtime' ? <Wifi size={14} /> : <WifiOff size={14} />}
                    Live transcript
                  </span>
                  <small>{status.activeSession.conversationPublicId}</small>
                </header>

                {lastSignal ? (
                  <div className="red-line-incoming-signal" role="status">
                    <Signal size={14} /> Visitor signal: {lastSignal}
                  </div>
                ) : null}

                <div className="red-line-admin-messages" aria-live="polite">
                  {messages.length === 0 ? (
                    <div className="red-line-admin-empty">
                      <Radio size={20} />
                      <span>The channel is open. Awaiting transmission.</span>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <article
                        key={message.publicId}
                        className={`red-line-admin-message red-line-admin-message--${message.sender} red-line-admin-message--${message.emphasis.toLowerCase()}`}
                      >
                        <header>
                          <strong>
                            {message.sender === 'admin' ? 'YOU' : message.sender.toUpperCase()}
                          </strong>
                          <time dateTime={message.createdAt}>
                            {new Date(message.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </time>
                        </header>
                        {message.contextChip ? <small>{message.contextChip}</small> : null}
                        <Markdown value={message.bodyMarkdown} />
                      </article>
                    ))
                  )}
                  {visitorTyping ? (
                    <div className="red-line-visitor-typing">Visitor is typing…</div>
                  ) : null}
                  <div ref={messagesEndRef} />
                </div>

                <form className="red-line-admin-compose" onSubmit={sendMessage}>
                  <textarea
                    value={messageInput}
                    onChange={(event) => {
                      setMessageInput(event.target.value);
                      realtimeRef.current?.setTyping(Boolean(event.target.value.trim()));
                    }}
                    maxLength={MAX_MESSAGE_LENGTH}
                    rows={2}
                    placeholder="Reply on the Red Line…"
                    disabled={messageBusy}
                    aria-label="Red Line reply"
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
                        event.preventDefault();
                        event.currentTarget.form?.requestSubmit();
                      }
                    }}
                  />
                  <Button
                    type="submit"
                    disabled={messageBusy || !messageInput.trim()}
                    aria-label="Send Red Line reply"
                  >
                    <Send />
                  </Button>
                </form>
              </div>
            </>
          ) : null}
        </>
      ) : (
        <div className="detail-loading">
          <LoaderCircle /> Checking…
        </div>
      )}

      <div className="red-line-controls">
        {status && !status.blocked && status.availabilityState === 'CLOSED' ? (
          <div className="red-line-open-controls">
            {RED_LINE_OPEN_DURATIONS.map((duration) => (
              <Button
                key={duration}
                variant="secondary"
                disabled={busy}
                onClick={() => void open(duration)}
              >
                {DURATION_LABELS[duration] ?? `${duration}s`}
              </Button>
            ))}
          </div>
        ) : null}

        {status && !status.blocked && status.availabilityState !== 'CLOSED' ? (
          <div className="red-line-action-row">
            <Button variant="secondary" disabled={busy} onClick={() => void endSession()}>
              End session
            </Button>
            <Button variant="secondary" disabled={busy} onClick={() => void convert()}>
              Convert to standard
            </Button>
            <div className="red-line-close-wrap">
              <Button
                variant="secondary"
                disabled={busy}
                onClick={() => setShowClosePresets((v) => !v)}
              >
                Close line
              </Button>
              {showClosePresets ? (
                <div className="red-line-close-presets">
                  {RED_LINE_CLOSING_PRESETS.map((preset) => (
                    <button key={preset} disabled={busy} onClick={() => void close(preset)}>
                      {preset}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {status && status.activeSession ? (
          <div className="red-line-signals">
            {RED_LINE_SIGNAL_REACTIONS.map((reaction) => (
              <button
                key={reaction}
                disabled={busy}
                onClick={() => void sendSignal(reaction)}
                title={reaction}
              >
                {reaction}
              </button>
            ))}
          </div>
        ) : null}

        {status && status.blocked ? (
          <Button variant="secondary" disabled={busy} onClick={() => void unblock()}>
            Unblock
          </Button>
        ) : (
          <Button variant="danger" disabled={busy} onClick={() => void block()}>
            Block
          </Button>
        )}
      </div>

      {error ? (
        <div className="panel-error">
          <CircleAlert /> {error}
        </div>
      ) : null}
    </section>
  );
}
